import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { logUpsert, logDelete } from "../../lib/audit";
import {
  listTicketTypes,
  createTicketType,
  updateTicketType,
  deleteTicketType,
  duplicateTicketType,
} from "@v0y4ge/api-client";
import type { TicketTypeDto } from "@v0y4ge/api-contracts";
import { t as i18nT } from "../../lib/i18n";
import { isSignedIn, getActiveTenantSlug } from "../../shell/access";

/**
 * Ticket Types — first-class library store + helpers.
 *
 * WIRED (kit cb3a9a13): the store is a client-backed optimistic cache. `load()`
 * hydrates once from `listTicketTypes()`; mutations apply immediately to the
 * cache (so the UI is instant + every consumer stays byte-for-byte) and persist
 * through the typed client (`create/update/delete/duplicateTicketType`) in the
 * background, reconciling the cache with the returned DTO.
 *
 * Status reconcile: contract `status` is 'active'|'inactive'; the UI uses
 * 'active'|'archived'. Mapped at this boundary (inactive↔archived) so the screen
 * stays verbatim.
 */
export type TicketKind = "free" | "paid" | "donation" | "invite";
export interface TicketType {
  id: string;
  name: string;
  kind: TicketKind;
  price: number;
  color: string;
  desc: string;
  defaultAllocation: number;
  status: "active" | "archived";
  /** Admin-only: when true, `price` is locked at the type level and cannot be
   * changed per event in the create/edit wizard. */
  priceLocked?: boolean;
}

export const TT_PALETTE = [T.tDarkBlue, T.tPurple, T.tPink, T.tOrange, T.tGreen, T.tTurquoise, T.tLightBlue, T.tRed];
let _ttSeq = 0;
const ttUid = () => `tt_${Date.now().toString(36)}${(_ttSeq++).toString(36)}`;
export const ttMoney = (n: number) => (Number(n) ? `$${Number(n).toLocaleString()}` : "Free");
export const TT_KINDS: { id: TicketKind; label: string; labelKey: string }[] = [
  { id: "free", labelKey: "tickets.typesStore.free", get label() { return i18nT("tickets.typesStore.free", "Free"); } },
  { id: "paid", labelKey: "tickets.typesStore.paid", get label() { return i18nT("tickets.typesStore.paid", "Paid"); } },
  { id: "donation", labelKey: "tickets.typesStore.donation", get label() { return i18nT("tickets.typesStore.donation", "Donation"); } },
  { id: "invite", labelKey: "tickets.typesStore.inviteOnly", get label() { return i18nT("tickets.typesStore.inviteOnly", "Invite-only"); } },
];
export const ttKindPill = (kind: TicketKind): { tone: "success" | "warn" | "primary" | "neutral"; label: string } =>
  kind === "paid" ? { tone: "success", label: i18nT("tickets.typesStore.paid", "Paid") }
    : kind === "donation" ? { tone: "warn", label: i18nT("tickets.typesStore.donation", "Donation") }
      : kind === "invite" ? { tone: "primary", label: i18nT("tickets.typesStore.inviteOnly", "Invite-only") }
        : { tone: "neutral", label: i18nT("tickets.typesStore.free", "Free") };

/* ── Contract adapter (DTO ↔ UI) ───────────────────────────────────────── */
function ttFromDto(d: TicketTypeDto): TicketType {
  return {
    id: d.id,
    name: d.name,
    kind: d.kind as TicketKind,
    price: typeof d.price === "number" ? d.price : (d.priceMinor ?? 0) / 100,
    color: d.color || TT_PALETTE[0],
    desc: d.desc ?? d.description ?? "",
    defaultAllocation: d.defaultAllocation ?? 100,
    status: d.status === "inactive" ? "archived" : "active",
    priceLocked: (d as { priceLocked?: boolean; price_locked?: boolean }).priceLocked ?? (d as { price_locked?: boolean }).price_locked ?? false,
  };
}
/** Session guard for creation. §3h (2026-07-19): NO fictional fallback — no
 *  signed-in session blocks creation.
 *
 *  NOTE (node rebuild, JAI-930): this is a CLIENT-SIDE GUARD ONLY. It is no
 *  longer sent. `TicketTypeCreateInput` is `.strict()` and carries no node
 *  reference — the catalog worker derives the owning node from the authenticated
 *  session, exactly as it does for services (a client-supplied node id was a
 *  privilege-escalation path). No window global is read (see ttSessionReady). */
/* SESSION, NOT A WINDOW GLOBAL (CC, 28 Sept): nothing in the deployed app sets
 * __STUDIO_NODE_ID__ / __STUDIO_COMPANY_ID__ (only the preview did), so every
 * deployed "Create type" was refused before any request. The node id is not
 * sent anyway — the worker takes it from the session — so the guard now asks
 * only what the rest of Studio asks: is someone signed in to a tenant. */
function ttSessionReady(): boolean {
  return isSignedIn() && !!getActiveTenantSlug();
}
function ttToCreateInput(t: TicketType) {
  if (!ttSessionReady()) throw new Error("No authenticated session identity — ticket type creation is unavailable until sign-in provides one.");
  /* JAI-2354: priced by KIND only. A lock on a free or invite type locks the price at 0; the
     server refuses a price on those kinds (422 TICKET_TIER_PRICE_INVALID). */
  const priced = t.kind === "paid" || t.kind === "donation";
  const major = priced ? Number(t.price) || 0 : 0;
  return {
    name: t.name.trim(), kind: t.kind,
    price: major, price_minor: Math.round(major * 100),
    price_locked: !!t.priceLocked, priceLocked: !!t.priceLocked,
    currency: "USD", default_allocation: t.defaultAllocation, defaultAllocation: t.defaultAllocation,
    desc: t.desc, description: t.desc, color: t.color,
    status: (t.status === "archived" ? "inactive" : "active") as "active" | "inactive",
    sort_order: 0, sortOrder: 0, settings: {},
  };
}
function ttToUpdateInput(t: TicketType) {
  const priced = t.kind === "paid" || t.kind === "donation"; // JAI-2354: by kind only

  return {
    name: t.name.trim(), kind: t.kind, price: priced ? Number(t.price) || 0 : 0,
    price_locked: !!t.priceLocked, priceLocked: !!t.priceLocked,
    currency: "USD", defaultAllocation: t.defaultAllocation,
    desc: t.desc, color: t.color,
    status: (t.status === "archived" ? "inactive" : "active") as "active" | "inactive",
  };
}
const ttUnwrap = (r: unknown): TicketTypeDto => {
  const o = r as { data?: TicketTypeDto } & TicketTypeDto;
  return (o && o.data ? o.data : o) as TicketTypeDto;
};

export const TicketTypeStore = (() => {
  let _types: TicketType[] = [];
  let _hydrated = false;
  const _alias = new Map<string, string>();
  const _inflight = new Map<string, Promise<TicketType>>();
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((l) => l());
  const idx = (id: string) => _types.findIndex((t) => t.id === id);
  const hydrate = () => {
    if (_hydrated) return;
    _hydrated = true;
    try {
      if (typeof listTicketTypes !== "function") return;
      Promise.resolve(listTicketTypes({}))
        .then((r: unknown) => {
          const o = r as { data?: TicketTypeDto[]; ticketTypes?: TicketTypeDto[] };
          const arr = o?.data ?? o?.ticketTypes;
          if (Array.isArray(arr)) { _types = arr.map(ttFromDto); notify(); }
        })
        .catch(() => { /* keep empty — real data only */ });
    } catch { /* keep seed */ }
  };
  return {
    all: (): TicketType[] => { hydrate(); return _types.slice(); },
    get: (id: string): TicketType | null => { hydrate(); return _types.find((t) => t.id === id) || null; },
    // Awaited client write FIRST; the cache updates only from the resolved
    // response (was optimistic + fire-and-forget — sweep #714 item 13).
    // Create exactly once (2026-09-30, Greg). A new form carries a CLIENT id
    // (ttUid); the server answers with its own. Before this, the client id was
    // never matched again, so every autosave tick and the Create click each
    // POSTed another type — the live tenant's duplicate rows. Now: writes for one
    // form id are serialized, and a client id resolves to the server id it
    // created, so every later write is an update.
    upsert: (t: TicketType): Promise<TicketType> => {
      const key = t.id;
      const prev = _inflight.get(key) ?? Promise.resolve();
      const run = prev.catch(() => undefined).then(async () => {
        const realId = _alias.get(key) ?? key;
        const i = idx(realId);
        logUpsert("ticketType", i >= 0 ? (_types[i] as unknown as Record<string, unknown>) : null, t as unknown as Record<string, unknown>, { name: t.name });
        const dto = i >= 0 ? await updateTicketType(realId, ttToUpdateInput(t)) : await createTicketType(ttToCreateInput(t));
        const mapped = ttFromDto(ttUnwrap(dto));
        if (i < 0 && mapped.id && mapped.id !== key) _alias.set(key, mapped.id);
        const j = idx(mapped.id);
        if (j >= 0) _types[j] = mapped;
        else _types = [mapped, ..._types];
        notify();
        return mapped;
      });
      _inflight.set(key, run);
      void run.finally(() => { if (_inflight.get(key) === run) _inflight.delete(key); }).catch(() => undefined);
      return run;
    },
    /** The server id a form's client id was created as, if it has been. */
    resolveId: (id: string): string => _alias.get(id) ?? id,
    remove: async (id: string) => {
      const prev = _types.find((x) => x.id === id);
      await deleteTicketType(id);
      if (prev) logDelete("ticketType", prev as unknown as Record<string, unknown>, { name: prev.name });
      _types = _types.filter((t) => t.id !== id); notify();
    },
    duplicate: async (id: string): Promise<TicketType | null> => {
      const t = _types.find((x) => x.id === id); if (!t) return null;
      const dto = await duplicateTicketType(id);
      const mapped = ttFromDto(ttUnwrap(dto));
      _types = [mapped, ..._types]; notify();
      return mapped;
    },
    subscribe: (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
    newType: (): TicketType => ({ id: ttUid(), name: "", kind: "free", price: 0, color: TT_PALETTE[0], desc: "", defaultAllocation: 100, status: "active", priceLocked: false }),
  };
})();

export function useTicketTypes(): TicketType[] {
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = TicketTypeStore.subscribe(() => force((n) => n + 1));
    return () => { unsub(); };
  }, []);
  return TicketTypeStore.all();
}

export const ttInput = (extra: React.CSSProperties = {}): React.CSSProperties => ({ width: "100%", height: 40, padding: "0 12px", boxSizing: "border-box", border: `1px solid ${T.n4}`, borderRadius: 8, fontFamily: T.font, fontSize: 14, color: T.n1, background: T.white, outline: "none", ...extra });
export const ttTh: React.CSSProperties = { textAlign: "left", padding: "10px 16px", whiteSpace: "nowrap" };
export const ttTd: React.CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };
export const ttLabel: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: T.n1, marginBottom: 6 };
